import type { SkinColors } from './skins'

/**
 * Ракетка. Источник — настоящий векторный SVG (public/game/racket.svg,
 * детали обработки в его шапке: кадр вырезан из листа ассетов Gemini,
 * довёрнут до вертикали, трассирован через vtracer). В игре, однако,
 * рисуется не он, а растеризованный из него PNG того же каталога:
 * на этой связке Chrome у `drawImage()` SVG → canvas → canvas (через
 * промежуточный bitmap, см. историю PR) воспроизводимо течёт на уровне
 * GPU-композитинга — сам canvas-буфер корректен (подтверждено
 * getImageData с альфа-каналом), а то, что реально попадает на экран,
 * иногда зарастает тёмным прямоугольником поверх прозрачного интерьера
 * головы. Через растровый `<img>` без промежуточного canvas этого нет.
 * Так что SVG — поддерживаемый источник (открывается и правится в любом
 * векторном редакторе), PNG — то, что реально гоняет canvas.
 *
 * Вид из-за спины игрока: рукоять уходит за нижний край экрана, как будто
 * её держит рука. Только картинка: зона отбоя — горизонтальный отрезок в
 * state.ts, и ни наклон, ни форма головы на неё не влияют.
 */

/** Наклон на единицу скорости ракетки, рад·с/ед. При 800 ед./с — потолок. */
const LEAN_PER_SPEED = 0.00025
const MAX_LEAN = 0.2

/**
 * Постоянный наклон плоскости ракетки вперёд, к сетке, рад. Вид из-за спины
 * почти ортографический, поэтому проецируем его сжатием по вертикали —
 * голова становится приплюснутой, как у настоящей ракетки в стойке.
 */
const FORWARD_TILT = 0.62

/**
 * Метрики спрайта в его собственных пикселях (748×1580 после вырезки и
 * довода до вертикали — с запасом по краям на каждом шаге обработки:
 * самый первый вырез из листа ассетов тоже обязан быть с запасом, а не
 * вплотную к контуру — иначе антиалиасинг по краю обода срезается
 * безвозвратно и наклон/паддинг на последующих шагах его уже не вернёт.
 * Центр головы и её наружные радиусы — наружный край рамки по x считается
 * краем зоны отбоя.
 */
const SPRITE = {
  url: '/game/racket.png',
  srcW: 748,
  srcH: 1580,
  cx: 336,
  cy: 359,
  rx: 258,
  ry: 296,
}

/** Высота головы от ширины — пропорции спрайта. */
const HEAD_ASPECT = SPRITE.ry / SPRITE.rx

let spriteImg: HTMLImageElement | null = null
let spriteReady = false
if (typeof Image !== 'undefined') {
  spriteImg = new Image()
  spriteImg.onload = () => {
    spriteReady = true
  }
  spriteImg.src = SPRITE.url
}

/**
 * Наклон от скорости: голову, которую тянут за рукоять, заносит назад, то есть
 * рукоять уходит вперёд по ходу движения.
 */
export function racketLean(vx: number): number {
  return Math.max(-MAX_LEAN, Math.min(MAX_LEAN, -vx * LEAN_PER_SPEED))
}

export function drawRacket(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  halfWidth: number,
  lean: number,
  skin: SkinColors,
): void {
  const w = halfWidth
  const h = w * HEAD_ASPECT
  // Единиц мира на пиксель спрайта: наружный радиус рамки = край зоны отбоя.
  const k = w / SPRITE.rx

  ctx.save()
  ctx.translate(x, y)
  // Тень лежит на корте и не наклоняется вместе с ракеткой.
  drawShadow(ctx, w, h)
  // Поворот вокруг центра головы: при наклоне голова остаётся над зоной
  // отбоя, а рукоять уходит в сторону.
  ctx.rotate(lean)
  // Наклон вперёд — после поворота, чтобы сжимать экранную вертикаль,
  // а не ось самой ракетки (иначе боковой наклон дал бы сдвиг).
  ctx.scale(1, Math.cos(FORWARD_TILT))
  if (spriteImg && spriteReady) {
    ctx.drawImage(spriteImg, -SPRITE.cx * k, -SPRITE.cy * k, SPRITE.srcW * k, SPRITE.srcH * k)
  } else {
    // Картинка ещё не загрузилась (первые кадры) — простая обводка эллипсом,
    // цвет берём из скина, чтобы не мелькал чужеродный серый силуэт.
    drawFrameFallback(ctx, w, h, skin)
  }
  ctx.restore()
}

function drawShadow(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.save()
  // Свет сверху-сзади: тень падает ближе к камере и чуть вправо.
  ctx.translate(6, h * 0.8)
  ctx.scale(1, 0.32)
  const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, w * 1.05)
  grad.addColorStop(0, 'rgba(30, 50, 80, 0.2)')
  grad.addColorStop(1, 'rgba(30, 50, 80, 0)')
  ctx.fillStyle = grad
  ctx.beginPath()
  ctx.arc(0, 0, w * 1.05, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

function drawFrameFallback(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  skin: SkinColors,
): void {
  ctx.beginPath()
  ctx.ellipse(0, 0, w, h, 0, 0, Math.PI * 2)
  ctx.strokeStyle = skin.frame
  ctx.lineWidth = 4
  ctx.stroke()
}
